import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useUser } from '@fs/zion-user'
import { useLazyRequest } from '../hooks/useRequest'
import { registrationConfig } from '../config'
import {
  getSubscriptions,
  getRegistrationStatus,
  registerForARC,
  registerForDiscoveryDay,
  easyRegister,
  registerForExpoHall,
  register,
  subscribe,
  updateSubscriptions,
  DUPLICATE_REGISTRATION_STATUS,
} from '../api/rootstech/registration'

const initialState = {
  hasSmsSubscription: false,
  hasOnlineRegistration: false,
  hasInPersonRegistration: false,
  hasOnlineSubscription: false,
  hasInPersonSubscription: false,
  hasArcRegistration: false,
  hasArcSubscription: false,
  hasExpoHallRegistration: false,
  hasDiscoveryDayRegistration: false,
}

const SUBSCRIPTION_NAME_MAP = {
  'RootsTech for SMS/MMS': 'hasSmsSubscription',
  'Online RootsTech Event': 'hasOnlineRegistration',
  'Online RootsTech Marketing': 'hasOnlineSubscription',
  'In-Person RootsTech Event': 'hasInPersonRegistration',
  'In-Person RootsTech Marketing': 'hasInPersonSubscription',
  'ARC Symposium Event': 'hasArcRegistration',
  'ARC Symposium Marketing': 'hasArcSubscription',
  'Free Expo Hall': 'hasExpoHallRegistration',
  'Free Family Day': 'hasDiscoveryDayRegistration',
}

const mapSubscriptionsFromAPI = (data) => {
  if (!data) {
    return initialState
  }

  const subscriptionGroups = data?.brazeUserSubscriptionResponse?.users?.[0]?.subscription_groups || []

  // Create lookup map for O(n) complexity
  const subscriptionLookup = subscriptionGroups.reduce((acc, group) => {
    const mappedName = SUBSCRIPTION_NAME_MAP[group.name]
    if (mappedName) {
      acc[mappedName] = group.status === 'Subscribed'
    }
    return acc
  }, {})

  return {
    ...initialState,
    ...subscriptionLookup,
  }
}
const computeDerivedProperties = (subscriptions) => {
  const hasAnyInPersonRegistration =
    subscriptions.hasInPersonRegistration ||
    subscriptions.hasExpoHallRegistration ||
    subscriptions.hasDiscoveryDayRegistration

  const hasAnyRegistration =
    subscriptions.hasOnlineRegistration ||
    subscriptions.hasInPersonRegistration ||
    subscriptions.hasArcRegistration ||
    subscriptions.hasDiscoveryDayRegistration ||
    subscriptions.hasExpoHallRegistration

  const hasAnySubscription =
    subscriptions.hasOnlineSubscription ||
    subscriptions.hasInPersonSubscription ||
    subscriptions.hasArcSubscription ||
    subscriptions.hasSmsSubscription

  // See docs/decisions/CDR-004-in-person-implies-online-registration.md
  const hasOnlineRegistration = subscriptions.hasOnlineRegistration || hasAnyInPersonRegistration

  const hasBothMainRegistrations = hasOnlineRegistration && subscriptions.hasInPersonRegistration
  const hasOnlyOnlineRegistration = hasOnlineRegistration && !subscriptions.hasInPersonRegistration
  const hasOnlyInPersonRegistration = !hasOnlineRegistration && subscriptions.hasInPersonRegistration

  return {
    hasOnlineRegistration,
    hasAnyInPersonRegistration,
    hasAnyRegistration,
    hasAnySubscription,
    hasBothMainRegistrations,
    hasOnlyOnlineRegistration,
    hasOnlyInPersonRegistration,
  }
}

// A missing response means the request never reached the server (network blip); a 5xx means
// the server failed transiently. Neither is worth retrying for a 4xx like an expired session
// or an unsupported account -- that just repeats the same failure every interval forever.
const isTransientFailure = (error) => !error?.response || error.response.status >= 500

// Fetches once per sign-in and retries on an interval after a transient failure, until it
// succeeds, `isSignedIn` changes, or the caller unmounts. Shared by useSubscriptionData and
// useRegistrationStatusData below so they retry the same way.
const useAutoRetryingFetch = (fetchFn, isSignedIn) => {
  const [data, setData] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [fetchAttempt, setFetchAttempt] = useState(0)

  const retry = useCallback(() => setFetchAttempt((attempt) => attempt + 1), [])

  useEffect(() => {
    if (!isSignedIn) {
      return undefined
    }

    let isMounted = true
    let retryTimeout

    const handleFailure = (error) => {
      console.error('Failed to fetch data:', error)
      if (isMounted && isTransientFailure(error)) {
        retryTimeout = setTimeout(retry, registrationConfig.statusFetchRetryDelayMs)
      }
    }

    const handleSuccess = (response) => {
      if (!isMounted) {
        return
      }
      if (response.error) {
        handleFailure(response.error)
        return
      }
      setData(response.data)
    }

    setLoading(true)
    fetchFn()
      .then(handleSuccess, handleFailure)
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
      clearTimeout(retryTimeout)
    }
  }, [isSignedIn, fetchAttempt, fetchFn, retry])

  return { data, loading, refetch: retry }
}

const useSubscriptionData = (isSignedIn) => {
  const { data, loading, refetch } = useAutoRetryingFetch(getSubscriptions, isSignedIn)
  const subscriptions = useMemo(() => mapSubscriptionsFromAPI(data), [data])

  return { subscriptions, loading, refetch }
}

// See docs/decisions/CDR-001-online-registration-status-source.md (including the amendment)
const useRegistrationStatusData = (isSignedIn) => {
  const { data } = useAutoRetryingFetch(getRegistrationStatus, isSignedIn)
  const [hasConfirmedOnlineRegistration, setHasConfirmedOnlineRegistration] = useState(false)

  const hasCurrentEventRegistration = hasConfirmedOnlineRegistration || Boolean(data?.current)
  const currentEventRegistrationUnknown = isSignedIn && !hasConfirmedOnlineRegistration && data === undefined
  const markRegistered = useCallback(() => setHasConfirmedOnlineRegistration(true), [])

  return {
    hasCurrentEventRegistration,
    hasConfirmedOnlineRegistration,
    currentEventRegistrationUnknown,
    markRegistered,
  }
}

// See docs/decisions/CDR-002-online-registration-trusts-post-response.md
const isNotDuplicateRegistrationError = (error) => error?.response?.status !== DUPLICATE_REGISTRATION_STATUS

/**
 * Creates a registration handler that automatically refreshes subscriptions on success
 * @param {Function} registrationFn - Registration function from useLazyRequest
 * @param {Function} onSuccess - Callback to run on successful registration
 * @param {Object} [options]
 * @param {boolean} [options.treatDuplicateAsSuccess] - See docs/decisions/CDR-002-online-registration-trusts-post-response.md
 * @returns {Function} Wrapped registration function
 */
const useAutoRefreshingSubscriptions = (registrationFn, onSuccess, { treatDuplicateAsSuccess = false } = {}) => {
  return useCallback(
    async (body) => {
      try {
        const response = await registrationFn(body)
        if (response && !response.error) {
          onSuccess()
        }
        return response
      } catch (error) {
        if (treatDuplicateAsSuccess && error?.response?.status === DUPLICATE_REGISTRATION_STATUS) {
          onSuccess()
        }
        throw error
      }
    },
    [registrationFn, onSuccess, treatDuplicateAsSuccess]
  )
}

const RegistrationContext = createContext(null)

export const useRegistrationContext = () => {
  const context = useContext(RegistrationContext)
  if (!context) {
    throw new Error('useRegistrationContext must be used within RegistrationProvider')
  }
  return context
}

/**
 * Provides registration state and actions throughout the app
 * Automatically manages subscription data fetching and refresh
 */
export function RegistrationProvider({ children }) {
  const { signedIn } = useUser()
  const { subscriptions, loading, refetch } = useSubscriptionData(signedIn)
  const {
    hasCurrentEventRegistration,
    hasConfirmedOnlineRegistration,
    currentEventRegistrationUnknown,
    markRegistered,
  } = useRegistrationStatusData(signedIn)

  // Lazy request hooks for each registration type
  const [sendOnlineRegistration] = useLazyRequest(register, undefined, isNotDuplicateRegistrationError)
  const [sendExpoHallRegistration] = useLazyRequest(registerForExpoHall)
  const [sendARCRegistration] = useLazyRequest(registerForARC)
  const [sendDiscoveryDayRegistration] = useLazyRequest(registerForDiscoveryDay)
  const [sendEasyRegistration] = useLazyRequest(easyRegister)
  const [sendSubscription] = useLazyRequest(subscribe)
  const [sendSubscriptionUpdates] = useLazyRequest(updateSubscriptions)

  // See docs/decisions/CDR-002-online-registration-trusts-post-response.md
  const onCurrentEventRegistrationSuccess = useCallback(() => {
    markRegistered()
    refetch()
  }, [markRegistered, refetch])

  // Auto-refreshing registration handlers
  const registerForOnlineEvent = useAutoRefreshingSubscriptions(
    sendOnlineRegistration,
    onCurrentEventRegistrationSuccess,
    { treatDuplicateAsSuccess: true }
  )
  const registerForExpoHallEvent = useAutoRefreshingSubscriptions(sendExpoHallRegistration, refetch)
  const registerForARCEvent = useAutoRefreshingSubscriptions(sendARCRegistration, refetch)
  const registerForDiscoveryDayEvent = useAutoRefreshingSubscriptions(sendDiscoveryDayRegistration, refetch)
  const registerWithEasyRegistration = useAutoRefreshingSubscriptions(
    sendEasyRegistration,
    onCurrentEventRegistrationSuccess,
    { treatDuplicateAsSuccess: true }
  )
  const subscribeToMarketing = useAutoRefreshingSubscriptions(sendSubscription, refetch)
  const updateMarketingPreferences = useAutoRefreshingSubscriptions(sendSubscriptionUpdates, refetch)

  const derivedProperties = useMemo(() => computeDerivedProperties(subscriptions), [subscriptions])

  const value = useMemo(
    () => ({
      // Registration actions
      registerForOnlineEvent,
      registerForExpoHallEvent,
      registerForARCEvent,
      registerForDiscoveryDayEvent,
      registerWithEasyRegistration,
      subscribeToMarketing,
      updateMarketingPreferences,

      // Individual properties (spread subscriptions directly)
      ...subscriptions,

      // Derived properties
      ...derivedProperties,

      // See docs/decisions/CDR-001-online-registration-status-source.md
      hasCurrentEventRegistration,
      hasConfirmedOnlineRegistration,
      currentEventRegistrationUnknown,

      // Loading state (useful for showing loading indicators)
      loading,
    }),
    [
      registerForOnlineEvent,
      registerForExpoHallEvent,
      registerForARCEvent,
      registerForDiscoveryDayEvent,
      registerWithEasyRegistration,
      subscribeToMarketing,
      updateMarketingPreferences,
      subscriptions,
      derivedProperties,
      hasCurrentEventRegistration,
      hasConfirmedOnlineRegistration,
      currentEventRegistrationUnknown,
      loading,
    ]
  )

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>
}
